import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAppAuth } from '@/contexts/app-auth.context'
import { getThreadsClient } from '@/lib/api/threads.client'

interface DeeplinkParams {
  threadId?: string | null
}

/**
 * Hook that resolves company context from deeplink params before returning auth state.
 * For super admins with a deeplink threadId, waits until switchSuperAdminCompany resolves
 * (state is then updated in app-auth-context).
 */
export function useResolvedThreadAppAuth(params: DeeplinkParams = {}) {
  const { threadId } = params
  const [searchParams] = useSearchParams()

  const appAuth = useAppAuth()
  const {
    isSuperAdmin,
    currentCompany,
    switchSuperAdminCompany,
    isCompanyLoading: baseCompanyLoading = false,
    isAuthLoading = false,
  } = appAuth

  // True once our deeplink resolution has completed (switch resolved or thread not found)
  const [resolved, setResolved] = useState(false)
  // Company the deeplink thread belongs to (for useChatData to skip around-query when user switched)
  const [deeplinkThreadCompanyId, setDeeplinkThreadCompanyId] = useState<
    string | null
  >(null)
  const fetchingRef = useRef(false)
  const hasCleanedUp = useRef(false)

  // Resolve company when we have a threadId: fetch thread, switch if needed, wait for switch to complete
  useEffect(() => {
    if (!threadId || !isSuperAdmin || isAuthLoading || baseCompanyLoading) {
      return
    }

    if (fetchingRef.current || resolved) {
      return
    }

    fetchingRef.current = true

    const resolve = async () => {
      try {
        const threadsClient = getThreadsClient(true)
        const thread = await threadsClient.getThreadById(threadId)
        const companyId = thread?.companyId

        if (companyId) {
          setDeeplinkThreadCompanyId(companyId)
          if (companyId !== currentCompany?.id) {
            const company = await switchSuperAdminCompany(companyId)
            // When switchSuperAdminCompany resolves, app-auth-context state is updated
            setResolved(!!company)
          } else {
            setResolved(true)
          }
        } else {
          setResolved(true)
        }
      } catch {
        // Thread not found - fall through to normal behavior
        setResolved(true)
      } finally {
        fetchingRef.current = false
      }
    }

    resolve()
  }, [
    threadId,
    isSuperAdmin,
    isAuthLoading,
    baseCompanyLoading,
    currentCompany?.id,
    switchSuperAdminCompany,
    resolved,
  ])

  // Reset resolved when threadId changes (e.g. new deeplink)
  const prevThreadIdRef = useRef(threadId)
  useEffect(() => {
    if (threadId !== prevThreadIdRef.current) {
      prevThreadIdRef.current = threadId
      if (!threadId || !isSuperAdmin) {
        setResolved(true)
        setDeeplinkThreadCompanyId(null)
      } else {
        setResolved(false)
        setDeeplinkThreadCompanyId(null)
        fetchingRef.current = false
      }
    }
  }, [threadId, isSuperAdmin])

  // Clean up URL params after resolution
  useEffect(() => {
    if (hasCleanedUp.current) return

    const hasDeeplinkParams = !!(
      searchParams.get('threadId') ||
      searchParams.get('scrollTo') ||
      searchParams.get('messageId') ||
      searchParams.get('sessionId') ||
      searchParams.get('companyId')
    )

    const needsResolution = threadId && isSuperAdmin
    const isResolved = !needsResolution || resolved

    if (!hasDeeplinkParams || !isResolved) {
      return
    }

    const timer = setTimeout(() => {
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('threadId')
      newParams.delete('scrollTo')
      newParams.delete('messageId')
      newParams.delete('sessionId')
      newParams.delete('companyId')
      const newUrl = newParams.toString()
        ? `${window.location.pathname}?${newParams.toString()}`
        : window.location.pathname
      window.history.replaceState({}, '', newUrl)
      hasCleanedUp.current = true
    }, 2000)

    return () => clearTimeout(timer)
  }, [searchParams, threadId, isSuperAdmin, resolved])

  // Loading until base auth is ready and (if deeplink) our resolution has completed
  const needsResolution = !!(threadId && isSuperAdmin)
  const isCompanyLoading =
    baseCompanyLoading || isAuthLoading || (needsResolution && !resolved)

  return {
    ...appAuth,
    isCompanyLoading,
    currentCompany: isCompanyLoading ? undefined : currentCompany,
    deeplinkThreadCompanyId,
  }
}
